import React from 'react';
import { Avatar } from '@mui/material';
import { ArrowForward, CalendarTodayOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeTeamName } from '../TablaResultadosVerticalEquipo';
import { getMatchdays, getMatchdayNumber } from '../../utils/matchdays';
import { buildActaNavigation } from '../../utils/matchNavigation';
import { reportNumber } from '../../utils/rivalReport';

export function TeamMatchLink({ match, round, team, className, children }) {
  const { t } = useTranslation('stats');
  const partido = {
    ...match,
    idTemporada: match.idTemporada ?? match.idtemporada ?? round?.idTemporada ?? round?.idtemporada ?? team?.idTemporada,
    idCompeticion: match.idCompeticion ?? round?.idCompeticion ?? team?.idCompeticion,
    idGrupo: match.idGrupo ?? round?.idGrupo ?? team?.idGrupo,
    idJornada: match.idJornada ?? round?.idJornada ?? round?.id,
    jornada: match.jornada ?? getMatchdayNumber(round),
  };
  const acta = match.partidoJugado !== false
    && [match.golesCasa, match.golesVisitante].every(value => Number.isInteger(reportNumber(value)))
    ? buildActaNavigation(partido, round, team?.idTemporada, team?.minutosJuego) : null;
  return <Link className={className} to={acta?.pathname || '/informePartido'}
    state={acta?.state || { partido }}
    aria-label={t('teams.modern.calendar.openMatch', { home: match.equipoLocal, away: match.equipoVisitante })}>
    {children}
  </Link>;
}

function fixtureDate(value, language) {
  if (!value) return '';
  const text = String(value);
  const local = text.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = local ? new Date(+local[3], +local[2] - 1, +local[1])
    : iso ? new Date(+iso[1], +iso[2] - 1, +iso[3]) : new Date(text);
  return Number.isNaN(date.getTime()) ? text
    : date.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function TeamStatisticsMatches({ team, calendar }) {
  const { t, i18n } = useTranslation('stats');
  const selected = String(team?.idEquipo ?? team?.id ?? '');
  const matches = getMatchdays(calendar).flatMap(round => (round.partidos || []).flatMap(match => {
    const homeId = match.codEquipoLocal ?? match.idEquipoLocal;
    const awayId = match.codEquipoVisitante ?? match.idEquipoVisitante;
    const hasIds = homeId != null && awayId != null && selected !== '';
    const home = hasIds ? String(homeId) === selected
      : !!team?.nombre && normalizeTeamName(match.equipoLocal) === normalizeTeamName(team.nombre);
    const away = hasIds ? String(awayId) === selected
      : !!team?.nombre && normalizeTeamName(match.equipoVisitante) === normalizeTeamName(team.nombre);
    return home || away ? [{ match, round, home, number: getMatchdayNumber(round) }] : [];
  })).sort((a, b) => (a.number ?? Infinity) - (b.number ?? Infinity));

  return <section className="ts-calendar" aria-label={t('teams.modern.calendar.title')}>
    <header className="ts-section-heading">
      <span className="rr-eyebrow">{t('teams.modern.calendar.eyebrow')}</span>
      <h2>{t('teams.modern.calendar.title')}</h2>
      <p>{t('teams.modern.calendar.subtitle')}</p>
    </header>
    {!matches.length ? <p className="rr-panel rr-empty">{t('teams.modern.calendar.empty')}</p>
      : <ol className="ts-match-list">{matches.map(({ match, round, home, number }, index) => {
        const opponent = home ? match.equipoVisitante : match.equipoLocal;
        const rest = ['Equipo Casa (No asignado)', 'Equipo Fuera (No asignado)'].includes(opponent);
        const played = match.partidoJugado !== false
          && [match.golesCasa, match.golesVisitante].every(value => Number.isInteger(reportNumber(value)));
        const difference = (Number(match.golesCasa) - Number(match.golesVisitante)) * (home ? 1 : -1);
        const outcome = !played ? 'pending' : difference > 0 ? 'win' : difference < 0 ? 'loss' : 'draw';
        const content = <>
          <span className="ts-match-round">{t('teams.modern.round', { round: number ?? '—' })}</span>
          <span className="ts-match-main">
            <span className="ts-match-meta">
              <span><CalendarTodayOutlined fontSize="inherit" />{[
                fixtureDate(match.fecha, i18n.language), match.hora?.slice(0, 5),
              ].filter(Boolean).join(' · ') || t('teams.modern.calendar.schedulePending')}</span>
              {!rest && <span>{t(`teams.modern.${home ? 'home' : 'away'}`)}</span>}
              <span className={`ts-match-outcome ts-match-outcome-${outcome}`}>
                {rest ? t('teams.modern.calendar.rest')
                  : t(played ? `teams.modern.outcomes.${outcome}` : 'teams.modern.calendar.pending')}
              </span>
            </span>
            {rest ? <strong>{t('teams.modern.calendar.rest')}</strong> : <span className="ts-match-fixture">
              <span className={`ts-match-team${home ? ' ts-match-selected' : ''}`}>
                <Avatar src={match.escudoEquipoLocal || undefined} alt="">{match.equipoLocal?.[0]}</Avatar>
                <span>{match.equipoLocal}</span>
              </span>
              <strong className="ts-match-score">{played ? `${match.golesCasa} – ${match.golesVisitante}` : '–'}</strong>
              <span className={`ts-match-team ts-match-away${!home ? ' ts-match-selected' : ''}`}>
                <Avatar src={match.escudoEquipoVisitante || undefined} alt="">{match.equipoVisitante?.[0]}</Avatar>
                <span>{match.equipoVisitante}</span>
              </span>
            </span>}
          </span>
          {!rest && <ArrowForward className="ts-match-arrow" fontSize="small" />}
        </>;
        return <li key={match.idPartido ?? match.id ?? `${number}:${index}`}>
          {rest ? <div className="ts-match-row ts-match-rest">{content}</div>
            : <TeamMatchLink match={match} round={round} team={team} className="ts-match-row">{content}</TeamMatchLink>}
        </li>;
      })}</ol>}
  </section>;
}
